import { CreditCard, Wallet } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

/**
 * #1805 「アプリで支払う」の中身を選ぶ (カード / YOKOSO 通貨)。
 *
 * **上の階層は「アプリで払うか、レジで払うか」**で、ここはその下。カードも
 * YOKOSO 通貨もアプリ内で完結する web 決済なので、同じ並びに置く
 * (Apple Pay / Google Pay はカードを選んだ後に Stripe の画面が自動で出す)。
 *
 * ポイントを受けない店では呼び元がそもそも出さない (`pointsAllowed` で判断)。
 * 現金のみ運用でポイントを受ける店 (#1769) はカードが無いのでポイントだけが並ぶ。
 * 残高不足でも選べる — 不足分はその場でチャージして払う (#1190 の QR と同じ)。
 */
export default function PayMethodToggle({
  method, onChange, cardAllowed, balance, amount, showTitle = true,
}: {
  method: 'card' | 'points'
  onChange: (m: 'card' | 'points') => void
  cardAllowed: boolean
  balance: number
  amount: number
  /** 呼び元が既に「お支払い方法」の見出しを出しているときは false */
  showTitle?: boolean
}) {
  const { t } = useI18n()
  const shortfall = Math.max(0, amount - balance)
  return (
    <div className="mb-4">
      {showTitle && (
        <p className="mb-1.5 font-body text-xs font-semibold text-text-secondary">
          {t('checkout.methodTitle')}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {cardAllowed && (
          <button
            type="button"
            onClick={() => onChange('card')}
            className={`h-14 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 tap-effect ${
              method === 'card' ? 'border-navy bg-navy/5 text-navy' : 'border-gray-200 text-text-secondary'
            }`}
          >
            <span className="flex items-center gap-1.5 font-display text-sm font-semibold">
              <CreditCard size={15} />
              {t('checkout.methodCard')}
            </span>
            <span className="font-body text-[10px] opacity-70">{t('checkout.methodCardSub')}</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => onChange('points')}
          data-testid="checkout-method-points"
          className={`${cardAllowed ? '' : 'col-span-2 '}h-14 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 tap-effect ${
            method === 'points' ? 'border-matcha bg-matcha/5 text-matcha' : 'border-gray-200 text-text-secondary'
          }`}
        >
          <span className="flex items-center gap-1.5 font-display text-sm font-semibold">
            <Wallet size={15} />
            {t('payment.methodPoints')}
          </span>
          <span className={`font-body text-[10px] ${shortfall > 0 ? 'text-red-500' : 'opacity-70'}`}>
            {shortfall > 0
              ? `${t('autoTopUp.shortfall')} ¥${shortfall.toLocaleString()}`
              : t('scan.balanceAvailable', { balance: balance.toLocaleString() })}
          </span>
        </button>
      </div>
    </div>
  )
}
